import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useToast } from '../../context/ToastContext';
import { Loader2 } from 'lucide-react';
import Logo from '../Logo';

const StudioSetupPassword: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Supabase client auto-processes invite tokens from the URL hash
    // before our code runs. Listen for auth state changes to catch the session.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        setIsReady(true);
      }
    });

    // Also check if session already exists (e.g. tokens already processed)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setIsReady(true);
      } else {
        // Give Supabase a moment to process the hash tokens
        setTimeout(async () => {
          const { data: { session: retrySession } } = await supabase.auth.getSession();
          if (retrySession) {
            setIsReady(true);
          } else {
            setError('No invite token found. Please use the link from your invitation email.');
          }
        }, 2000);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      showToast('Password set successfully! Welcome to Studio.', 'success');
      navigate('/studio');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white p-8 lg:p-10 rounded-[2.5rem] shadow-2xl border border-gray-100">
          <div className="text-center mb-10">
            <div className="flex justify-center mb-6">
              <Logo size="lg" showText={false} />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Welcome to Studio</h2>
            <p className="text-gray-500">Set a password to secure your account.</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm font-bold text-center">
              {error}
            </div>
          )}

          {!isReady && !error && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
            </div>
          )}

          {isReady && (
            <form onSubmit={handleSetPassword} className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-bold text-gray-700">Password</label>
                <input
                  required
                  type="password"
                  placeholder="At least 6 characters"
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none transition-all"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-bold text-gray-700">Confirm Password</label>
                <input
                  required
                  type="password"
                  placeholder="Re-enter your password"
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none transition-all"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-5 bg-teal-600 text-white rounded-full text-lg font-bold hover:bg-teal-700 transition-all shadow-lg hover:shadow-teal-500/20 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : 'Set Password & Continue'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudioSetupPassword;
